import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Flame,
  AlertTriangle,
  Ruler,
  Droplets,
  FileSpreadsheet,
  Home,
  FileCheck,
  ClipboardCheck,
  Clock,
  Phone,
  BarChart3
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <FileSpreadsheet className="h-12 w-12 text-dk-blue" />,
      title: "DPE (Diagnostic de Performance Énergétique)",
      context: "Vente & Location",
      description: "Évalue la performance énergétique du bien, sa consommation et son impact environnemental. Fournit une estimation de la facture énergétique."
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-dk-blue" />,
      title: "Audit Énergétique",
      context: "Vente",
      description: "Analyse approfondie proposant des scénarios de travaux pour améliorer la performance énergétique des logements classés E, F ou G. Complément obligatoire au DPE pour les passoires thermiques."
    },
    {
      icon: <AlertTriangle className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Amiante",
      context: "Vente & Location",
      description: "Identifie la présence d'amiante dans les matériaux de construction pour informer sur les risques pour la santé."
    },
    {
      icon: <Zap className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Électricité",
      context: "Vente & Location",
      description: "Évalue la sécurité de l'installation électrique. Obligatoire pour les installations de plus de 15 ans."
    },
    {
      icon: <Flame className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Gaz",
      context: "Vente & Location",
      description: "Évalue la sécurité de l'installation de gaz et prévient les accidents. Obligatoire pour les installations de plus de 15 ans."
    },
    {
      icon: <Home className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Termites",
      context: "Vente",
      description: "Inspection approfondie pour détecter la présence de termites et autres insectes xylophages."
    },
    {
      icon: <Ruler className="h-12 w-12 text-dk-blue" />,
      title: "Loi Carrez",
      context: "Vente en copropriété",
      description: "Calcul de la superficie privative des lots de copropriété. Obligatoire pour toute vente d'un bien en copropriété."
    },
    {
      icon: <Ruler className="h-12 w-12 text-dk-blue" />,
      title: "Loi Boutin",
      context: "Location",
      description: "Calcule la superficie habitable du logement loué. Obligatoire pour toute mise en location."
    },
    {
      icon: <Droplets className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Plomb (CREP)",
      context: "Vente & Location",
      description: "Évalue la présence de plomb dans les peintures. Obligatoire pour les logements construits avant 1949."
    },
    {
      icon: <FileCheck className="h-12 w-12 text-dk-blue" />,
      title: "État des Risques et Pollutions (ERP)",
      context: "Vente & Location",
      description: "Informe sur les risques naturels, technologiques et de pollution auxquels le bien est exposé selon sa zone géographique."
    }
  ];

  const [activeFilter, setActiveFilter] = useState<'all' | 'vente' | 'location'>('all');

  const filters = [
    { key: 'all' as const, label: 'Tous' },
    { key: 'vente' as const, label: 'Vente' },
    { key: 'location' as const, label: 'Location' },
  ];

  const filteredServices = services.filter((service) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'vente') {
      return service.context.includes('Vente');
    }
    return service.context.includes('Location');
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-24 pb-16"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Nos Services de Diagnostics Immobiliers</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Notre diagnostiqueur certifié se déplace à la demande des propriétaires, des bailleurs 
            et des différents acteurs de l'immobilier pour réaliser tous les diagnostics obligatoires 
            pour la vente et la location de biens.
          </p>
        </div>

        {/* Filtres Vente / Location */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeFilter === filter.key
                  ? 'bg-dk-blue text-white shadow-lg shadow-dk-blue/25'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Grille des services */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16"
        >
          <AnimatePresence mode="popLayout">
            {filteredServices.map((service) => (
              <motion.div
                key={service.title}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-transparent hover:border-dk-blue group"
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-dk-blue transition-colors">
                  {service.title}
                </h3>
                {service.context && (
                  <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${
                    service.context === 'Vente'
                      ? 'bg-blue-100 text-blue-700'
                      : service.context === 'Location'
                        ? 'bg-teal-100 text-teal-700'
                        : service.context === 'Vente en copropriété'
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-gray-100 text-gray-700'
                  }`}>
                    {service.context}
                  </span>
                )}
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Section Notre Force */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-white to-gray-100 rounded-xl p-8 shadow-lg mb-16 border border-gray-200"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Notre Force ?</h2>
              <div className="flex items-start space-x-4 mb-4">
                <Clock className="h-6 w-6 flex-shrink-0 mt-1 text-dk-blue" />
                <p className="text-lg text-gray-700">
                  Disponibilité et réactivité, avec un rapport rendu sous 72h.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <ClipboardCheck className="h-16 w-16 text-dk-blue" />
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">72h</p>
                <p className="text-gray-600">Délai de rendu</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Besoin d'un diagnostic ?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Contactez-nous pour obtenir un devis personnalisé ou prendre rendez-vous.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="tel:0789307500" 
              className="btn-primary flex items-center justify-center hover:bg-dk-blue/90 transition-colors"
            >
              <Phone className="h-5 w-5 mr-2" />
              Nous appeler
            </a>
            <Link 
              to="/contact" 
              className="btn bg-white text-dk-blue border-2 border-dk-blue hover:bg-dk-blue hover:text-white transition-colors"
            >
              Demander un devis
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Services;